import React, { useState } from 'react';
import './SupplierProductsModal.css';
import { getPlanConfig } from '../../utils/membershipUtils';
import { LOGO_PLACEHOLDER, PRODUCT_PLACEHOLDER } from '../../utils/placeholders';
import { useCurrency } from '../../context/CurrencyContext';

const SupplierProductsModal = ({ isOpen, onClose, supplier }) => {
  const { formatPriceRange } = useCurrency();
  const [activeTab, setActiveTab] = useState('home');

  if (!isOpen || !supplier) return null;

  // Parse JSON fields safely
  const parseJSON = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch {
      return [];
    }
  };

  const snapshots = parseJSON(supplier.snapshots);
  const brochures = parseJSON(supplier.brochures);
  const products = supplier.products || [];

  return (
    <div className="spm-overlay" onClick={onClose}>
      <div className="spm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="spm-close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Cover Image */}
        <div className="spm-cover-section">
          <img 
            src={supplier.coverImage || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200'} 
            alt="Cover" 
            className="spm-cover-image" 
          />
        </div>

        {/* Company Info Section */}
        <div className="spm-company-section">
          <div className="spm-company-header">
            <div className="spm-company-info">
              <img 
                src={supplier.logo || LOGO_PLACEHOLDER} 
                alt={supplier.name}
                className="spm-company-logo"
              />
              <div className="spm-company-details">
                <h2 className="spm-company-name">{supplier.name}</h2>
                <div className="spm-company-badges">
                  {(() => {
                    const planConfig = getPlanConfig(supplier.membershipPlan);
                    return (
                      <span className="spm-badge global" style={{ backgroundColor: planConfig.color }}>
                        <img src={planConfig.icon} alt={planConfig.label} style={{ width: '16px', height: '16px' }} />
                        {planConfig.label}
                      </span>
                    );
                  })()}
                  {supplier.isVerified && (
                    <span className="spm-badge verified">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Verified
                    </span>
                  )}
                  <span className="spm-badge location">
                    {supplier.countryFlag && <img src={supplier.countryFlag} alt={supplier.country} className="spm-flag" />}
                    {supplier.country} / {supplier.memberYears || 1} Year
                  </span>
                  <span className="spm-badge business-type">
                    {supplier.businessType || 'Manufacturer'}
                  </span>
                </div>
              </div>
            </div>
            <div className="spm-stats-section">
              <span className="spm-stat">{supplier.reviews || 0} Reviews</span>
              <div className="spm-stat-divider"></div>
              <span className="spm-stat">{products.length} Products</span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="spm-nav-tabs">
            <button 
              className={`spm-nav-tab ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => setActiveTab('home')}
            >
              Home
            </button>
            <button 
              className={`spm-nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Company Profile
            </button>
            <button 
              className={`spm-nav-tab ${activeTab === 'product' ? 'active' : ''}`}
              onClick={() => setActiveTab('product')}
            >
              Product
            </button>
            <button 
              className={`spm-nav-tab ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              Contact
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="spm-tab-content">
          {/* HOME TAB */}
          {activeTab === 'home' && (
            <>
              <div className="spm-content-row">
                <div className="spm-video-placeholder">
                  {supplier.videoUrl ? (
                    <iframe 
                      src={supplier.videoUrl} 
                      title="Company Video"
                      allowFullScreen
                    />
                  ) : (
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="spm-video-icon">
                      <circle cx="12" cy="12" r="10" stroke="#CCCCCC" strokeWidth="2"/>
                      <path d="M10 8l6 4-6 4V8z" fill="#CCCCCC"/>
                    </svg>
                  )}
                </div>
                <div className="spm-content-details">
                  <h3>{supplier.name}</h3>
                  <p className="spm-tagline">Trusted {supplier.businessType || 'Supplier'} with {supplier.memberYears || 1}+ Years of Industry Experience</p>
                  <p className="spm-description">
                    {supplier.description || supplier.companyOverview || 'A leading supplier committed to delivering high-quality products and services.'}
                  </p>
                  <div className="spm-contact-cta">
                    <h4>Contact to Seller</h4>
                    <p>Have questions or want to get started? Our team is here to help with support, product info, or partnership inquiries.</p>
                    <div className="spm-action-buttons">
                      <button className="spm-btn-primary" onClick={() => setActiveTab('contact')}>Send Inquiry</button>
                      <button className="spm-btn-secondary">Certifications & Trust Metrics</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Highlight */}
              {products.length > 0 && (
                <div className="spm-section">
                  <h3>Product Highlight</h3>
                  <div className="spm-products-grid">
                    {products.slice(0, 4).map((product, index) => (
                      <div key={product.id || index} className="spm-product-card">
                        <img 
                          src={product.image || PRODUCT_PLACEHOLDER} 
                          alt={product.name}
                        />
                        <div className="spm-product-info">
                          <h4>{product.name}</h4>
                          <p className="spm-product-price">
                            {formatPriceRange(product.priceMin || 0, product.priceMax || 0)} / {product.unit || 'Unit'}
                          </p>
                          <p className="spm-product-moq">MOQ: {product.moq || 'N/A'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Latest Product */}
              {products.length > 0 && (
                <div className="spm-section">
                  <h3>Latest Product</h3>
                  <div className="spm-products-grid full">
                    {products.slice(0, 8).map((product, index) => (
                      <div key={product.id || index} className="spm-product-card">
                        <img 
                          src={product.image || PRODUCT_PLACEHOLDER} 
                          alt={product.name}
                        />
                        <div className="spm-product-info">
                          <h4>{product.name}</h4>
                          <p className="spm-product-price">
                            {formatPriceRange(product.priceMin || 0, product.priceMax || 0)} / {product.unit || 'Unit'}
                          </p>
                          <p className="spm-product-moq">MOQ: {product.moq || 'N/A'}</p>
                          <p className="spm-product-category">{product.category?.name || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Company Snapshot */}
              {snapshots.length > 0 && (
                <div className="spm-section">
                  <h3>Company Snapshot</h3>
                  <div className="spm-snapshots-grid">
                    {snapshots.map((snapshot, index) => (
                      <img key={index} src={snapshot} alt={`Snapshot ${index + 1}`} className="spm-snapshot-image" />
                    ))}
                  </div>
                </div>
              )}

              {/* Brochure */}
              {brochures.length > 0 && (
                <div className="spm-section">
                  <h3>Brochure</h3>
                  <div className="spm-brochure">
                    {brochures.map((brochure, index) => (
                      <a key={index} href={brochure} target="_blank" rel="noopener noreferrer" className="spm-download-btn">
                        Download PDF
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* COMPANY PROFILE TAB */}
          {activeTab === 'profile' && (
            <>
              <div className="spm-section">
                <h3>Basic Information</h3>
                <div className="spm-info-grid">
                  <div className="spm-info-item">
                    <label>Company Name</label>
                    <span>{supplier.name}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>Business Type</label>
                    <span>{supplier.businessType || 'N/A'}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>Established Year</label>
                    <span>{supplier.establishYear || 'N/A'}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>Number of Employees</label>
                    <span>{supplier.numberOfEmployees || 'N/A'}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>Country</label>
                    <span>{supplier.country || 'N/A'}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>City</label>
                    <span>{supplier.city || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="spm-section">
                <h3>Contact Information</h3>
                <div className="spm-info-grid">
                  <div className="spm-info-item">
                    <label>Email</label>
                    <span className="spm-link">{supplier.email || 'N/A'}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>Phone</label>
                    <span>{supplier.phone || supplier.phoneNumber || 'N/A'}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>Website</label>
                    <span className="spm-link">{supplier.websiteLink || 'N/A'}</span>
                  </div>
                  <div className="spm-info-item">
                    <label>Address</label>
                    <span>{supplier.address || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {supplier.companyOverview && (
                <div className="spm-section">
                  <h3>Company Overview</h3>
                  <p className="spm-text-content">{supplier.companyOverview}</p>
                </div>
              )}
            </>
          )}

          {/* PRODUCT TAB */}
          {activeTab === 'product' && (
            <div className="spm-section">
              <h3>All Products ({products.length})</h3>
              {products.length > 0 ? (
                <div className="spm-products-grid full">
                  {products.map((product, index) => (
                    <div key={product.id || index} className="spm-product-card">
                      <img 
                        src={product.image || PRODUCT_PLACEHOLDER} 
                        alt={product.name}
                      />
                      <div className="spm-product-info">
                        <h4>{product.name}</h4>
                        <p className="spm-product-price">
                          {formatPriceRange(product.priceMin || 0, product.priceMax || 0)} / {product.unit || 'Unit'}
                        </p>
                        <p className="spm-product-moq">MOQ: {product.moq || 'N/A'}</p>
                        <p className="spm-product-category">{product.category?.name || ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="spm-no-products">
                  <p>No products available</p>
                </div>
              )}
            </div>
          )}

          {/* CONTACT TAB */}
          {activeTab === 'contact' && (
            <div className="spm-section">
              <h3>Contact Information</h3>
              <div className="spm-contact-card">
                <div className="spm-contact-items">
                  <div className="spm-contact-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#0397DD" strokeWidth="2"/>
                      <polyline points="22,6 12,13 2,6" stroke="#0397DD" strokeWidth="2"/>
                    </svg>
                    <span>{supplier.email || 'No email provided'}</span>
                  </div>
                  <div className="spm-contact-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="#0397DD" strokeWidth="2"/>
                    </svg>
                    <span>{supplier.phone || supplier.phoneNumber || 'No phone provided'}</span>
                  </div>
                  <div className="spm-contact-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="#0397DD" strokeWidth="2"/>
                      <circle cx="12" cy="10" r="3" stroke="#0397DD" strokeWidth="2"/>
                    </svg>
                    <span>{supplier.address || supplier.country || 'No address provided'}</span>
                  </div>
                  {supplier.websiteLink && (
                    <div className="spm-contact-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#0397DD" strokeWidth="2"/>
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#0397DD" strokeWidth="2"/>
                      </svg>
                      <a href={supplier.websiteLink} target="_blank" rel="noopener noreferrer">{supplier.websiteLink}</a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="spm-actions">
          <button className="spm-cancel-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default SupplierProductsModal;
